/**
 * M7 S14: station refusals under Tethys retaliation (spec §9). Each landed retaliation row (`activeRetaliationRows`,
 * S5) refuses some actions at some stations, from `hooks.json refusals`:
 *
 * - `dockingRefused` (every owned pilot): Tethys stations refuse docking, so every service there (market, board,
 *   yard) is refused too.
 * - `refinerySalesRefused` (mining income): the Refinery and the Export Terminal refuse sales.
 * - `boardsClosed` (combat income): Tethys and Varro contract boards close to the player.
 * - `componentsRefused` (salvage and trade income): Tethys stations refuse components and salvage.
 * - `yardsRefused` (salvage and trade income): Tethys yards refuse orders.
 *
 * Rows without a station refusal (the called principal, the hunt, insurance, interdiction, the corporation rows) are
 * absent from the config and never refuse here. A refusal never names a reason (the operator doesn't give one).
 *
 * Pure; the trade commands (`economy/trade.ts`) and the contract board (`contracts/board.ts`) call it, and the
 * runtime pre-checks it (`marketRefusal`) before committing a trade through the strategic worker.
 */
import { fail, type CmdErrorCode, type CmdFailure } from '../career/result'
import type { Career } from '../career/types'
import type { StationDef } from '../data/m2Schemas'
import templatesJson from '../data/strategic/templates.json'
import type { FactionId } from '../strategic/types'
import { z } from 'zod'
import { hooksConfig } from './hooks'
import { activeRetaliationRows } from './leverage'
import type { HooksConfig } from './schemas'
import { storyOf } from './state'
import { RETALIATION_ROWS, type RetaliationRow, type StationAction } from './types'

/** The station as the refusals read it: its owner and its M4 template (`templates.json` id), if known. */
export interface RefusalStation {
  faction: FactionId
  template: string | null
}

const templateRows = z.object({ templates: z.array(z.object({ id: z.string(), m2Id: z.string().nullable() })) })

let m2Templates: ReadonlyMap<string, string> | undefined

/** The M4 template an M2 station plays (`templates.json m2Id`), or null. */
export function templateOfM2(stationId: string): string | null {
  m2Templates ??= new Map(
    templateRows
      .parse(templatesJson)
      .templates.filter((t) => t.m2Id !== null)
      .map((t) => [t.m2Id ?? '', t.id]),
  )
  return m2Templates.get(stationId) ?? null
}

/** An M2 station as the refusals read it. */
export function m2RefusalStation(station: Pick<StationDef, 'id' | 'faction'>): RefusalStation {
  return { faction: station.faction, template: templateOfM2(station.id) }
}

/**
 * The retaliation row that refuses `action` at `station` now, or null. Rows are checked in `RETALIATION_ROWS` order,
 * so docking (which refuses everything) answers first.
 */
export function retaliationRefuses(
  career: Career,
  station: RefusalStation,
  action: StationAction,
  cfg: HooksConfig = hooksConfig(),
): RetaliationRow | null {
  if (storyOf(career).retaliation === undefined) return null
  const active = new Set(activeRetaliationRows(storyOf(career)))
  for (const row of RETALIATION_ROWS) {
    if (!active.has(row)) continue
    const rc = cfg.refusals[row]
    if (rc === undefined || !rc.factions.includes(station.faction) || !rc.actions.includes(action)) continue
    if (rc.templates.length > 0 && (station.template === null || !rc.templates.includes(station.template))) continue
    return row
  }
  return null
}

/** The operator's message for a refused row. */
export function refusalMessage(row: RetaliationRow, cfg: HooksConfig = hooksConfig()): string {
  return cfg.refusals[row]?.message ?? 'Service refused by order of the operator.'
}

const ACTION_CODE: Readonly<Record<StationAction, CmdErrorCode>> = {
  dock: 'serviceUnavailable',
  sellWare: 'notBoughtHere',
  buyWare: 'notSoldHere',
  sellComponent: 'notBoughtHere',
  buyComponent: 'notSoldHere',
  contracts: 'serviceUnavailable',
  yardOrder: 'serviceUnavailable',
}

/** The trade or service refusal at M2 station `station` (a typed `CmdFailure`), or null when it goes ahead. */
export function marketRefusal(
  career: Career,
  station: Pick<StationDef, 'id' | 'faction'>,
  action: StationAction,
  cfg: HooksConfig = hooksConfig(),
): CmdFailure | null {
  const row = retaliationRefuses(career, m2RefusalStation(station), action, cfg)
  if (row === null) return null
  return fail(ACTION_CODE[action], refusalMessage(row, cfg), { station: station.id, refusal: row })
}
